import { useMemo } from 'react';
import type { CatalogVerbs } from '@components/ui/catalog/ResourceCatalog/ResourceCatalog.types';
// Direct store-module imports (not the feature barrel): the module injects
// its endpoints into baseApi at load, which is exactly what we need — the
// catalog reuses the wizard's read + archive verbs (GDN-2335: zero new BE).
import {
  useBatchArchiveEncounterTypesMutation,
  useGetCleanupBootstrapQuery,
} from '@features/practice-cleanup/store/api';
import type { FixtureType } from '@features/practice-cleanup/types';
import { skipToken } from '@reduxjs/toolkit/query';

export interface TypeCatalogData {
  types: FixtureType[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  /**
   * Archive/restore over POST …/batch-archive, then an awaited refetch of
   * the bootstrap read so rows are fresh when the verb resolves (the
   * cleanup API has no cache tags — the catalog owns its own refresh).
   * Rejects on failure; ResourceCatalog handles the error affordance.
   */
  verbs: CatalogVerbs;
}

export const useTypeCatalogData = (
  orgId: string | undefined
): TypeCatalogData => {
  const query = useGetCleanupBootstrapQuery(orgId ? { orgId } : skipToken);
  const [batchArchive] = useBatchArchiveEncounterTypesMutation();

  const { refetch } = query;
  const verbs = useMemo<CatalogVerbs>(() => {
    const run = async (
      ids: string[],
      direction: 'archive' | 'restore'
    ): Promise<void> => {
      if (!orgId) throw new Error('No organization in scope');
      await batchArchive({
        orgId,
        body: {
          archive_type_ids: direction === 'archive' ? ids : [],
          restore_type_ids: direction === 'restore' ? ids : [],
          archive_template_ids: [],
        },
      }).unwrap();
      await refetch();
    };
    return {
      archive: (ids): Promise<void> => run(ids, 'archive'),
      restore: (ids): Promise<void> => run(ids, 'restore'),
    };
  }, [orgId, batchArchive, refetch]);

  return {
    types: query.data?.types ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: (): void => {
      void query.refetch();
    },
    verbs,
  };
};
